/**
 * Mẫu ảnh QR gửi khách — "Kem cổ điển", vẽ theo mockup CEO duyệt.
 *
 * Trước đây có 3 mẫu cho nhân sự chọn; CEO bỏ khung chọn 30/10/2026, chỉ giữ
 * mẫu kem. Mẫu là một hàm vẽ thuần: nhận canvas + dữ liệu phiếu, vẽ xong là
 * thôi — không tự nạp ảnh, không tự tạo canvas, không đụng React. Việc nạp logo
 * và dựng ma trận QR do `renderer.ts` lo.
 */

import { BRAND, POSTER_HEIGHT, POSTER_WIDTH } from "./brand";
import {
  drawPill,
  drawShield,
  drawText,
  formatPosterAmount,
  measureTextWidth,
} from "./canvas-kit";
import { drawQR, roundedRectPath, type QRMatrix } from "./qr-matrix";

/** Mã mẫu — giữ kiểu riêng để sau này thêm mẫu không phải sửa chỗ gọi. */
export type QRTemplateId = "classic";

export type PosterContent = {
  /** Số tiền phải chuyển, đồng. */
  amount: number;
  /** Nội dung chuyển khoản — ngân hàng khớp phiếu bằng đúng chuỗi này. */
  memo: string;
};

export type TemplateDrawContext = {
  ctx: CanvasRenderingContext2D;
  content: PosterContent;
  matrix: QRMatrix;
  /** Logo giữa mã QR (bản vuông, nền trắng). */
  emblem: HTMLImageElement;
  /** Logo đủ chữ, mực nâu — dùng trên nền sáng. */
  lockup: HTMLImageElement;
  /** Logo đủ chữ, mực trắng ngà — dùng trên nền nâu đậm (mẫu 2). */
  lockupLight: HTMLImageElement;
};

export type QRTemplate = {
  id: QRTemplateId;
  /** Tên hiện trên nút chọn mẫu. */
  label: string;
  /** Mô tả ngắn dưới tên, giúp nhân sự chọn nhanh. */
  hint: string;
  draw: (context: TemplateDrawContext) => void;
};

/** Vẽ logo đủ chữ, canh theo chiều cao cho trước, giữ nguyên tỉ lệ. */
function drawLockup(
  ctx: CanvasRenderingContext2D,
  lockup: HTMLImageElement,
  x: number,
  y: number,
  height: number,
): number {
  const ratio = lockup.width / lockup.height;
  const width = height * ratio;
  ctx.drawImage(lockup, x, y, width, height);
  return width;
}

/** Khối "Nội dung chuyển khoản" — 3 mẫu đặt khác chỗ nhưng cùng cách vẽ. */
function drawMemoBox(
  ctx: CanvasRenderingContext2D,
  memo: string,
  x: number,
  y: number,
  width: number,
  height: number,
  fill: string,
  textColor: string,
): void {
  roundedRectPath(ctx, x, y, width, height, 20);
  ctx.fillStyle = fill;
  ctx.fill();
  drawText(ctx, memo, x + 32, y + height / 2, {
    weight: 700,
    size: 34,
    color: textColor,
  });
}

/**
 * Mẫu 1 — nền kem, bố cục giữa.
 *
 * Mẫu "an toàn" nhất: sáng, ít màu, in ra giấy trắng đen vẫn đọc được. Đây là
 * mẫu mặc định.
 */
const classic: QRTemplate = {
  id: "classic",
  label: "Kem cổ điển",
  hint: "Nền sáng, bố cục giữa — dễ đọc, in giấy tốt",
  draw: ({ ctx, content, matrix, emblem, lockup }) => {
    ctx.fillStyle = BRAND.cream;
    ctx.fillRect(0, 0, POSTER_WIDTH, POSTER_HEIGHT);

    // Dải sáng phía trên, ôm phần đầu ảnh.
    ctx.fillStyle = BRAND.paper;
    ctx.fillRect(0, 0, POSTER_WIDTH, 232);

    drawLockup(ctx, lockup, 72, 56, 96);

    // Nhãn "đã xác thực" góc phải.
    //
    // Bề ngang ĐO TỪ CHỮ, không đặt số cứng: chữ tiếng Việt có dấu rộng hơn dự
    // đoán, mà bộ chữ dự phòng của máy còn rộng hơn nữa — đặt số cứng là vài
    // ký tự cuối rơi ra ngoài nền xanh.
    const badgeLabel = "MÃ ĐƯỢC XÁC THỰC";
    const badgeFont = { weight: 700, size: 21, letterSpacing: 0.6 } as const;
    const badgeTextW = measureTextWidth(ctx, badgeLabel, badgeFont);
    const badgeIconGap = 66; // chừa chỗ cho khiên + khoảng thở trái
    const badgeW = badgeIconGap + badgeTextW + 28;
    const badgeX = POSTER_WIDTH - 80 - badgeW;
    drawPill(ctx, {
      x: badgeX,
      y: 74,
      width: badgeW,
      height: 56,
      radius: 28,
      fill: BRAND.verdantSoft,
    });
    drawShield(ctx, badgeX + 40, 102, 30, BRAND.verdant, BRAND.white);
    drawText(ctx, badgeLabel, badgeX + badgeIconGap, 103, {
      ...badgeFont,
      color: BRAND.espresso,
    });

    // Thẻ trắng lớn chứa toàn bộ nội dung.
    const cardX = 72;
    const cardY = 168;
    const cardW = POSTER_WIDTH - cardX * 2;
    const cardH = POSTER_HEIGHT - cardY - 72;
    roundedRectPath(ctx, cardX, cardY, cardW, cardH, 40);
    ctx.fillStyle = BRAND.creamLight;
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = "#E7DCCB";
    ctx.stroke();

    const centerX = POSTER_WIDTH / 2;
    drawText(ctx, "QUÉT MÃ ĐỂ THANH TOÁN", centerX, 233, {
      weight: 700,
      size: 47,
      color: BRAND.espresso,
      align: "center",
    });

    // Ô số tiền.
    const amountText = formatPosterAmount(content.amount);
    const amountW = 358;
    drawPill(ctx, {
      x: centerX - amountW / 2,
      y: 310,
      width: amountW,
      height: 62,
      radius: 31,
      fill: "#F3E9D9",
    });
    drawText(ctx, amountText, centerX, 341, {
      weight: 700,
      size: 40,
      color: BRAND.espresso,
      align: "center",
    });

    // Khung QR.
    const frameX = 212;
    const frameY = 394;
    const frameSize = 610;
    roundedRectPath(ctx, frameX, frameY, frameSize, frameSize, 32);
    ctx.fillStyle = BRAND.paper;
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = BRAND.gold;
    ctx.stroke();

    const qrSize = 530;
    drawQR({
      ctx,
      matrix,
      x: frameX + (frameSize - qrSize) / 2,
      y: frameY + (frameSize - qrSize) / 2,
      size: qrSize,
      logo: emblem,
      foreground: "#000000",
      background: BRAND.paper,
    });

    // Nhãn + ô nội dung đặt DƯỚI đáy khung QR (frameY + frameSize = 1004).
    // Trước đây nhãn vẽ ở 1046 trong khi khung kéo tới 1050 → chữ đè lên viền
    // khung. Suy từ `frameY + frameSize` thay vì gõ số, để đổi cỡ khung là hai
    // khối này tự trôi theo.
    const memoLabelY = frameY + frameSize + 34;
    drawText(ctx, "Nội dung chuyển khoản", 128, memoLabelY, {
      weight: 400,
      size: 26,
      color: BRAND.muted,
    });
    drawMemoBox(ctx, content.memo, 128, memoLabelY + 24, 824, 62, BRAND.inkBox, BRAND.white);

    // Giữa ô nội dung và đáy thẻ: dặn khách giữ nguyên nội dung — ngân hàng
    // khớp phiếu thu bằng đúng chuỗi này, khách sửa đi là tiền về mà không
    // tự gạch được phiếu (CEO 30/10/2026).
    const cardBottom = cardY + cardH;
    const memoBottom = memoLabelY + 24 + 62;
    drawText(
      ctx,
      "Không được phép đổi nội dung chuyển khoản",
      centerX,
      (memoBottom + cardBottom) / 2,
      { weight: 700, size: 36, color: BRAND.danger, align: "center" },
    );
  },
};

/** Chỉ còn một mẫu — CEO bỏ khung chọn mẫu 30/10/2026, giữ "Kem cổ điển". */
export const QR_TEMPLATES: QRTemplate[] = [classic];

export const DEFAULT_TEMPLATE_ID: QRTemplateId = "classic";

export function templateById(id: QRTemplateId): QRTemplate {
  return QR_TEMPLATES.find((t) => t.id === id) ?? QR_TEMPLATES[0]!;
}
